// Real API functions for admin operations
import { getTierOptions } from '../../utils/tierUtils';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:9890/api';

// Helper function to get auth token
const getAuthToken = () => {
  return localStorage.getItem('token');
};

// Helper function to make authenticated API calls
const makeApiCall = async (endpoint, options = {}) => {
  const token = getAuthToken();
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...options.headers
    },
    ...options
  };

  const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Request failed' }));
    console.error('API Error Response:', errorData);
    
    // Show validation details if available
    if (errorData.details && Array.isArray(errorData.details)) {
      const validationErrors = errorData.details.map(detail => 
        `${detail.field}: ${detail.message}`
      ).join(', ');
      throw new Error(`${errorData.message}: ${validationErrors}`);
    }
    
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }
  
  return response.json();
};

export const fetchTournaments = async () => {
  try {
    const response = await makeApiCall('/tournaments');
    return response.data || [];
  } catch (error) {
    console.error('Error fetching tournaments:', error);
    throw error;
  }
};

export const createTournament = async (tournamentData) => {
  try {
    const response = await makeApiCall('/tournaments', {
      method: 'POST',
      body: JSON.stringify(tournamentData)
    });
    return response.data;
  } catch (error) {
    console.error('Error creating tournament:', error);
    throw error;
  }
};

export const updateTournament = async ({ id, ...tournamentData }) => {
  try {
    const response = await makeApiCall(`/tournaments/${id}`, {
      method: 'PUT',
      body: JSON.stringify(tournamentData)
    });
    return response.data;
  } catch (error) {
    console.error('Error updating tournament:', error);
    throw error;
  }
};

export const deleteTournament = async (id) => {
  try {
    const response = await makeApiCall(`/tournaments/${id}`, {
      method: 'DELETE'
    });
    return response;
  } catch (error) {
    console.error('Error deleting tournament:', error);
    throw error;
  }
};

export const completeTournament = async (id) => {
  try {
    const response = await makeApiCall(`/tournaments/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status: 'COMPLETED' })
    });
    return response;
  } catch (error) {
    console.error('Error completing tournament:', error);
    throw error;
  }
};

export const forceCompleteSimulation = async (id, simulationType = 'instant') => {
  try {
    const response = await makeApiCall(`/tournaments/${id}/force-complete-simulation`, {
      method: 'POST',
      body: JSON.stringify({ simulationType })
    });
    return response;
  } catch (error) {
    console.error('Error force completing simulation:', error);
    throw error;
  }
};

export const triggerPrizeSettlement = async (id) => {
  try {
    const response = await makeApiCall(`/tournaments/${id}/trigger-prize-settlement`, {
      method: 'POST'
    });
    return response;
  } catch (error) {
    console.error('Error triggering prize settlement:', error);
    throw error;
  }
};

export const validateTournamentCompletion = async (id) => {
  try {
    const response = await makeApiCall(`/tournaments/${id}/validate-completion`, {
      method: 'GET'
    });
    return response;
  } catch (error) {
    console.error('Error validating tournament completion:', error);
    throw error;
  }
};

export const duplicateTournament = async (sourceTournamentId, newName, startDate, endDate) => {
  try {
    const response = await makeApiCall('/tournaments/duplicate', {
      method: 'POST',
      body: JSON.stringify({
        sourceTournamentId,
        newName,
        startDate,
        endDate
      })
    });
    return response.data;
  } catch (error) {
    console.error('Error duplicating tournament:', error);
    throw error;
  }
};

export const fetchAnalyticsData = async (timeRange, tournamentId, tournaments) => {
  try {
    const response = await makeApiCall(`/analytics?timeRange=${timeRange}&tournamentId=${tournamentId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching analytics data:', error);
    return getFallbackAnalyticsData();
  }
};

// Fetch tournament financial analytics
export const fetchTournamentFinancials = async (tournamentId) => {
  try {
    const response = await makeApiCall(`/tournaments/${tournamentId}/financials`);
    return response.data;
  } catch (error) {
    console.error('Error fetching tournament financials:', error);
    throw error;
  }
};

// Fetch tournament pot distribution data (uses saved DB data, no recalculation)
export const fetchTournamentPotDistribution = async (tournamentId) => {
  try {
    const response = await makeApiCall(`/tournaments/${tournamentId}/pot-distribution`);
    return response.data;
  } catch (error) {
    console.error('Error fetching tournament pot distribution:', error);
    throw error;
  }
};

// Fallback data in case API fails
const getFallbackAnalyticsData = () => ({
  overview: {
    totalRevenue: 0,
    revenueChange: 0,
    activeTournaments: 0,
    tournamentChange: 0,
    totalParticipants: 0,
    participantChange: 0,
    commissionEarned: 0,
    commissionChange: 0
  },
  financial: {
    playerPurchases: 0,
    tierAPurchases: 0,
    tierBPurchases: 0,
    tierCPurchases: 0,
    tierDPurchases: 0,
    platformCommission: 0,
    commissionRate: 20,
    netRevenue: 0
  },
  participants: {
    ageRange: 'N/A',
    genderDistribution: 'N/A',
    geographicSpread: 'N/A',
    avgTournaments: 0,
    repeatPlayers: '0%',
    newPlayers: '0%',
    avgPlayerPurchase: 0,
    premiumAdoption: '0%',
    lifetimeValue: 0
  },
  comparison: {
    revenue: []
  }
});

// Real API function to fetch golf tiers from backend
export const fetchPlayerTiers = async () => {
  try {
    const response = await makeApiCall('/tiers');
    return response.data || [];
  } catch (error) {
    console.error('Error fetching tier settings:', error);
    // Fallback to default golf tiers if API fails
    return [
      {
        tier: 'TIER_A',
        tierName: 'Tier A',
        rankingMin: 1,
        rankingMax: 30,
        defaultPrice: 250.00,
        description: 'Top 30 golfers according to World Golf Rankings'
      },
      {
        tier: 'TIER_B',
        tierName: 'Tier B',
        rankingMin: 31,
        rankingMax: 60,
        defaultPrice: 200.00,
        description: 'Golfers ranked 31-60'
      },
      {
        tier: 'TIER_C',
        tierName: 'Tier C',
        rankingMin: 61,
        rankingMax: 90,
        defaultPrice: 150.00,
        description: 'Golfers ranked 61-90'
      },
      {
        tier: 'TIER_D',
        tierName: 'Tier D',
        rankingMin: 91,
        rankingMax: null,
        defaultPrice: 100.00,
        description: 'All remaining golfers (91+)'
      }
    ];
  }
};

export const createPlayerTier = async (tierData) => {
  // Golf tiers are predefined (TIER_A, TIER_B, TIER_C, TIER_D)
  // This function is not used for golf tiers - they are managed by the system
  throw new Error('Golf tiers are predefined and cannot be created via this function');
};

export const updatePlayerTier = async (tier, tierData) => {
  try {
    const response = await makeApiCall(`/tiers/${tier}`, {
      method: 'PUT',
      body: JSON.stringify(tierData)
    });
    return response;
  } catch (error) {
    console.error('Error updating tier settings:', error);
    throw error;
  }
};

export const deletePlayerTier = async (id) => {
  // Golf tiers (TIER_A, TIER_B, TIER_C, TIER_D) are predefined and cannot be deleted
  throw new Error('Golf tiers are predefined system tiers and cannot be deleted');
};

// This function is not used - we use fetchAvailableGolfers for real player data
// Keeping for backward compatibility but with correct tier names
export const fetchPlayers = async () => {
  // This is deprecated - use fetchAvailableGolfers instead
  console.warn('fetchPlayers is deprecated - use fetchAvailableGolfers instead');
  return [];
};

export const initializeTierSettings = async () => {
  try {
    const response = await makeApiCall('/tiers/initialize', {
      method: 'POST'
    });
    return response;
  } catch (error) {
    console.error('Error initializing tier settings:', error);
    throw error;
  }
};

export const assignPlayersToTier = async (tierId, playerIds) => {
  // This function is for assigning specific players to tiers
  // For golf, players are automatically assigned based on rankings
  console.warn('Manual tier assignment - players should be assigned based on rankings');
  return { 
    success: true, 
    message: `Players should be assigned automatically based on world rankings`,
    assignedPlayers: []
  };
};

// Tournament player management functions
export const fetchTournamentPlayers = async (tournamentId) => {
  try {
    const response = await makeApiCall(`/tournaments/${tournamentId}/players`);
    return response.data?.players || [];
  } catch (error) {
    console.error('Error fetching tournament players:', error);
    throw error;
  }
};

export const fetchHomepageGolfers = async () => {
  try {
    const response = await makeApiCall('/golfers?limit=10');
    return response.data || [];
  } catch (error) {
    console.error('Error fetching available golfers:', error);
    throw error;
  }
};

export const fetchAvailableGolfers = async () => {
  try {
    const response = await makeApiCall('/golfers?limit=1000');
    return response.data || [];
  } catch (error) {
    console.error('Error fetching available golfers:', error);
    throw error;
  }
};

// Create a new golfer
export const createGolfer = async (golferData) => {
  try {
    const response = await makeApiCall('/golfers', {
      method: 'POST',
      body: JSON.stringify(golferData)
    });
    return response.data;
  } catch (error) {
    console.error('Error creating golfer:', error);
    throw error;
  }
};

// Update a golfer
export const updateGolfer = async (id, golferData) => {
  try {
    const response = await makeApiCall(`/golfers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(golferData)
    });
    return response.data;
  } catch (error) {
    console.error('Error updating golfer:', error);
    throw error;
  }
};

// Delete a golfer
export const deleteGolfer = async (id) => {
  try {
    const response = await makeApiCall(`/golfers/${id}`, {
      method: 'DELETE'
    });
    return response;
  } catch (error) {
    console.error('Error deleting golfer:', error);
    throw error;
  }
};

// Update golfer tier
export const updateGolferTier = async (id, tier) => {
  try {
    const response = await makeApiCall(`/golfers/${id}/tier`, {
      method: 'PUT',
      body: JSON.stringify({ tier })
    });
    return response.data;
  } catch (error) {
    console.error('Error updating golfer tier:', error);
    throw error;
  }
};

// Bulk assign tier to multiple golfers
export const bulkAssignTier = async (golferIds, tier) => {
  try {
    const response = await makeApiCall('/golfers/bulk-assign-tier', {
      method: 'POST',
      body: JSON.stringify({ golferIds, tier })
    });
    return response.data;
  } catch (error) {
    console.error('Error bulk assigning tier:', error);
    throw error;
  }
};

export const addPlayerToTournament = async (tournamentId, golferId, tier = 'TIER_D') => {
  try {
    const response = await makeApiCall(`/tournaments/${tournamentId}/players/add`, {
      method: 'POST',
      body: JSON.stringify({ golferId, tier })
    });
    return response.data;
  } catch (error) {
    console.error('Error adding player to tournament:', error);
    throw error;
  }
};

export const removePlayerFromTournament = async (tournamentId, golferId) => {
  try {
    const response = await makeApiCall(`/tournaments/${tournamentId}/players/${golferId}`, {
      method: 'DELETE'
    });
    return response;
  } catch (error) {
    console.error('Error removing player from tournament:', error);
    throw error;
  }
};

export const updateTournamentPlayerTier = async (tournamentId, golferId, tier) => {
  try {
    const response = await makeApiCall(`/tournaments/${tournamentId}/players/${golferId}/tier`, {
      method: 'PUT',
      body: JSON.stringify({ tier })
    });
    return response.data;
  } catch (error) {
    console.error('Error updating player tier:', error);
    throw error;
  }
};

export const initializeTournamentLeaderboard = async (tournamentId) => {
  try {
    const response = await makeApiCall(`/tournaments/${tournamentId}/init-leaderboard`, {
      method: 'POST'
    });
    return response.data;
  } catch (error) {
    console.error('Error initializing leaderboard:', error);
    throw error;
  }
};

export const updateLeaderboardWithNewPlayers = async (tournamentId) => {
  try {
    const response = await makeApiCall(`/tournament-players/${tournamentId}/players/update-leaderboard`, {
      method: 'POST'
    });
    return response.data;
  } catch (error) {
    console.error('Error updating leaderboard with new players:', error);
    throw error;
  }
};

// Get tier options for forms
export const getTierOptionsForForms = () => {
  return getTierOptions();
};

// Sync players from OWGR
export const syncPlayersFromOWGR = async () => {
  try {
    const response = await makeApiCall('/golfers/sync-owgr', {
      method: 'POST'
    });
    return response.data || { newPlayers: 0, updatedPlayers: 0 };
  } catch (error) {
    console.error('Error syncing players from OWGR:', error);
    throw error;
  }
};

// Sync players from SlashGolf API
export const syncPlayersFromSlashGolf = async () => {
  try {
    const response = await makeApiCall('/golfers/sync-slashgolf', {
      method: 'POST'
    });
    return response.data || { newPlayers: 0, updatedPlayers: 0 };
  } catch (error) {
    console.error('Error syncing players from SlashGolf API:', error);
    throw error;
  }
};

// Sync tournament players from SlashGolf API
export const syncTournamentPlayersFromSlashGolf = async (tournamentId) => {
  try {
    const response = await makeApiCall(`/tournaments/${tournamentId}/players/sync-from-slashgolf`, {
      method: 'POST'
    });
    return response.data || { added: 0, existing: 0, total: 0, skipped: 0 };
  } catch (error) {
    console.error('Error syncing tournament players:', error);
    throw error;
  }
};

export const fetchPGATourTournaments = async () => {
  try {
    // This will call our backend script that fetches from NBC Sports
    const response = await makeApiCall('/admin/fetch-pga-tournaments', {
      method: 'POST'
    });
    return response.tournaments || [];
  } catch (error) {
    console.error('Error fetching PGA Tour tournaments:', error);
    throw error;
  }
};

export const createTournamentsFromPGATour = async (pgaTournaments) => {
  try {
    const results = [];
    
    for (const pgaTournament of pgaTournaments) {
      try {
        // Format the tournament data to match our existing API structure
        const tournamentData = {
          name: pgaTournament.name,
          description: `Venue: ${pgaTournament.venue}\nLast Winner: ${pgaTournament.lastWinner}\nPurse: ${pgaTournament.purse}`,
          startDate: pgaTournament.startDate,
          endDate: pgaTournament.endDate,
          maxPlayers: 156,
          status: 'DRAFT',
          potSize: 0,
          tournamentFormat: 'STROKE_PLAY',
          prizeDistribution: [],
          roundsConfig: [],
          playerRequirements: {},
          scoringRules: {}
        };

        // Use existing create tournament API
        const result = await createTournament(tournamentData);
        results.push({ success: true, tournament: result, original: pgaTournament });
      } catch (error) {
        console.error(`Error creating tournament ${pgaTournament.name}:`, error);
        results.push({ success: false, error: error.message, original: pgaTournament });
      }
    }

    return results;
  } catch (error) {
    console.error('Error creating tournaments from PGA Tour data:', error);
    throw error;
  }
};

// Fetch users for admin management
export const fetchUsers = async () => {
  try {
    const response = await makeApiCall('/users');
    return response.users || [];
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

// Update user role
export const updateUserRole = async (userId, role) => {
  try {
    const response = await makeApiCall(`/users/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role })
    });
    return response.user;
  } catch (error) {
    console.error('Error updating user role:', error);
    throw error;
  }
};

// Update user status
export const updateUserStatus = async (userId, isActive) => {
  try {
    const response = await makeApiCall(`/users/${userId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ isActive })
    });
    return response.user;
  } catch (error) {
    console.error('Error updating user status:', error);
    throw error;
  }
};

// Fetch tournament schedule from SlashGolf API
export const fetchSlashGolfSchedule = async () => {
  try {
    const response = await makeApiCall('/tournaments/slashgolf-schedule', {
      method: 'GET'
    });
    return response.data || { tournaments: [], total: 0 };
  } catch (error) {
    console.error('Error fetching SlashGolf schedule:', error);
    throw error;
  }
};

// Import tournaments from SlashGolf API
export const importSlashGolfTournaments = async (tournaments) => {
  try {
    const response = await makeApiCall('/tournaments/import-slashgolf', {
      method: 'POST',
      body: JSON.stringify({ tournaments })
    });
    return response.data || { imported: 0, skipped: 0, total: 0, errors: [] };
  } catch (error) {
    console.error('Error importing SlashGolf tournaments:', error);
    throw error;
  }
};

// Fetch scorecard data from SlashGolf API
export const fetchScorecardSlashgolf = async (tournamentId) => {
  try {
    const response = await makeApiCall(`/tournaments/${tournamentId}/fetch-scorecard`, {
      method: 'POST'
    });
    return response;
  } catch (error) {
    console.error('Error fetching scorecard from SlashGolf:', error);
    throw error;
  }
};

// Process all skins and saw-offs for qualifying holes
export const processAllSkinsAndSawOffs = async (tournamentId) => {
  try {
    const response = await makeApiCall(`/tournaments/${tournamentId}/process-all-skins`, {
      method: 'POST'
    });
    return response;
  } catch (error) {
    console.error('Error processing skins and saw-offs:', error);
    throw error;
  }
};

// Flush all Redis cache
export const flushRedisCache = async () => {
  try {
    const response = await makeApiCall('/admin/cache/flush', {
      method: 'POST'
    });
    return response;
  } catch (error) {
    console.error('Error flushing Redis cache:', error);
    throw error;
  }
};

// Submit a single player score for a specific round and hole
export const submitPlayerScore = async (tournamentId, golferId, round, hole, score, par = 4) => {
  try {
    const response = await makeApiCall(`/tournaments/${tournamentId}/scores`, {
      method: 'POST',
      body: JSON.stringify({
        golferId,
        round: parseInt(round),
        hole: parseInt(hole),
        score: parseInt(score),
        par: parseInt(par)
      })
    });
    return response;
  } catch (error) {
    console.error('Error submitting player score:', error);
    throw error;
  }
};

// Fetch hole events for a tournament
export const fetchHoleEvents = async (tournamentId) => {
  try {
    const response = await makeApiCall(`/tournaments/${tournamentId}/hole-events`);
    return response.data;
  } catch (error) {
    console.error('Error fetching hole events:', error);
    throw error;
  }
};

// Delete a single hole event
export const deleteHoleEvent = async (tournamentId, eventId) => {
  try {
    const response = await makeApiCall(`/tournaments/${tournamentId}/hole-events/${eventId}`, {
      method: 'DELETE'
    });
    return response;
  } catch (error) {
    console.error('Error deleting hole event:', error);
    throw error;
  }
};

// Delete all hole events for a tournament
export const deleteAllHoleEvents = async (tournamentId) => {
  try {
    const response = await makeApiCall(`/tournaments/${tournamentId}/hole-events`, {
      method: 'DELETE'
    });
    return response;
  } catch (error) {
    console.error('Error deleting all hole events:', error);
    throw error;
  }
};